import React, { useState, useEffect, useRef } from 'react';
import JSZip from 'jszip';
import {
  Loader2, Film, Eye, Play, RefreshCw, Filter,
  CheckCircle, AlertCircle, Clock, Users, Search,
  Download, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Image as ImageIcon,
  Layers, FolderKanban, Clapperboard,
} from 'lucide-react';
import {
  getAdminImageMotionProjects,
  getAdminImageMotionStats,
  getAdminProjectImages,
  ingestProjectVideo,
  type AdminImageMotionProject,
  type AdminImageMotionStats,
  type AdminProjectImage,
} from '../services/supabase';
import ModelsPricingPanel from './admin/ModelsPricingPanel';
import StagingAnimationSettings from './admin/StagingAnimationSettings';
import StagingAnimationsList from './admin/StagingAnimationsList';
import { AdminShell } from '@rehub/admin/ui';

interface AdminPanelProps {
  onClose: () => void;
  adminRole: 'admin' | 'superadmin' | 'editor';
}

type AdminSection = 'projects' | 'staging_animations' | 'models' | 'staging_animation';

const STATUS_COLORS: Record<string, string> = {
  completed: 'bg-green-900/40 text-green-400',
  failed: 'bg-red-900/40 text-red-400',
  rendering: 'bg-blue-900/40 text-blue-400',
  generating_clips: 'bg-blue-900/40 text-blue-400',
  editing: 'bg-indigo-900/40 text-indigo-400',
  queued: 'bg-slate-700/60 text-slate-400',
  expired: 'bg-orange-900/40 text-orange-400',
};

function ImageRow({ projectId, projectName }: { projectId: string; projectName: string }) {
  const [images, setImages] = useState<AdminProjectImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [downloadingAll, setDownloadingAll] = useState(false);

  useEffect(() => {
    (async () => {
      const data = await getAdminProjectImages(projectId);
      setImages(data);
      setLoading(false);
    })();
  }, [projectId]);

  const downloadImage = async (url: string, index: number) => {
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const ext = blob.type.includes('png') ? 'png' : blob.type.includes('webp') ? 'webp' : 'jpg';
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `${projectName.replace(/[^a-zA-Z0-9]/g, '_')}_image_${index + 1}.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('Image download failed:', err);
      window.open(url, '_blank');
    }
  };

  const downloadAll = async () => {
    setDownloadingAll(true);
    try {
      const zip = new JSZip();
      const safeName = projectName.replace(/[^a-zA-Z0-9]/g, '_');

      await Promise.all(
        images.map(async (img, i) => {
          try {
            const res = await fetch(img.image_url);
            if (!res.ok) return;
            const blob = await res.blob();
            const ext = blob.type.includes('png') ? 'png' : blob.type.includes('webp') ? 'webp' : 'jpg';
            zip.file(`${safeName}_image_${i + 1}.${ext}`, blob);
          } catch {
            // skip failed images
          }
        })
      );

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(zipBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${safeName}_images.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('ZIP download failed:', err);
    }
    setDownloadingAll(false);
  };

  if (loading) {
    return (
      <td colSpan={8} className="px-4 py-4">
        <div className="flex items-center gap-2 text-slate-400 text-xs">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          Loading images...
        </div>
      </td>
    );
  }

  if (images.length === 0) {
    return (
      <td colSpan={8} className="px-4 py-4">
        <p className="text-xs text-slate-500">No images found for this project</p>
      </td>
    );
  }

  return (
    <td colSpan={8} className="px-4 py-4">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs text-slate-400 font-medium">
          {images.length} image{images.length !== 1 ? 's' : ''}
        </span>
        <button
          onClick={downloadAll}
          disabled={downloadingAll}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-xs font-medium rounded-lg transition-colors"
        >
          {downloadingAll ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : (
            <Download className="w-3 h-3" />
          )}
          {downloadingAll ? 'Downloading...' : 'Download All'}
        </button>
      </div>
      <div className="flex flex-wrap gap-2">
        {images.map((img, i) => (
          <div
            key={img.id}
            className="relative group w-24 h-18 rounded-lg overflow-hidden border border-slate-700 bg-slate-800"
          >
            <img
              src={img.image_url}
              alt={`Image ${i + 1}`}
              className="w-full h-full object-cover"
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-colors flex items-center justify-center">
              <button
                onClick={() => downloadImage(img.image_url, i)}
                className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 bg-blue-600 rounded-full text-white"
                title={`Download image ${i + 1}`}
              >
                <Download className="w-3 h-3" />
              </button>
            </div>
            <span className="absolute bottom-0 inset-x-0 bg-black/70 text-center py-0.5 text-[9px] text-white font-medium">
              {i + 1}
            </span>
          </div>
        ))}
      </div>
    </td>
  );
}

const PROJECTS_PAGE_SIZE = 100;

function ProjectsSection() {
  const [projects, setProjects] = useState<AdminImageMotionProject[]>([]);
  const [stats, setStats] = useState<AdminImageMotionStats>({
    total: 0,
    uniqueUsers: 0,
    completed: 0,
    failed: 0,
    inProgress: 0,
    funnelPreviews: 0,
  });
  const [matchTotal, setMatchTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [expandedProjectId, setExpandedProjectId] = useState<string | null>(null);
  const [openingVideoId, setOpeningVideoId] = useState<string | null>(null);
  const requestId = useRef(0);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, statusFilter]);

  const loadStats = async () => {
    try {
      setStats(await getAdminImageMotionStats());
    } catch (e) {
      console.error('Failed to load project stats:', e);
    }
  };

  const loadPage = async (nextPage: number, status: string, search: string) => {
    const id = ++requestId.current;
    setLoading(true);
    try {
      const data = await getAdminImageMotionProjects({
        offset: nextPage * PROJECTS_PAGE_SIZE,
        limit: PROJECTS_PAGE_SIZE,
        status: status || undefined,
        search: search || undefined,
      });
      if (id !== requestId.current) return;
      setProjects(data.projects);
      setMatchTotal(data.total);
    } catch (e) {
      if (id !== requestId.current) return;
      console.error('Failed to load projects:', e);
    }
    if (id === requestId.current) setLoading(false);
  };

  useEffect(() => {
    loadPage(page, statusFilter, debouncedSearch);
  }, [page, statusFilter, debouncedSearch]);

  useEffect(() => {
    loadStats();
  }, []);

  const refresh = () => {
    loadStats();
    loadPage(page, statusFilter, debouncedSearch);
  };

  const pageStart = matchTotal === 0 ? 0 : page * PROJECTS_PAGE_SIZE + 1;
  const pageEnd = Math.min((page + 1) * PROJECTS_PAGE_SIZE, matchTotal);

  const toggleExpanded = (projectId: string) => {
    setExpandedProjectId((prev) => (prev === projectId ? null : projectId));
  };

  // Projects with no storage path only have a Shotstack link, which expires
  // ~24h after rendering. Copy it into storage first; if that fails it's gone.
  const playVideo = async (p: AdminImageMotionProject) => {
    if (p.final_video_path || !p.final_video_url) {
      if (p.final_video_url) window.open(p.final_video_url, '_blank', 'noopener');
      return;
    }
    const tab = window.open('', '_blank');
    setOpeningVideoId(p.id);
    const ingested = await ingestProjectVideo(p.id);
    setOpeningVideoId(null);
    if (ingested) {
      setProjects((prev) =>
        prev.map((row) =>
          row.id === p.id
            ? { ...row, final_video_url: ingested.signedUrl, final_video_path: ingested.path }
            : row,
        ),
      );
      if (tab) tab.location.href = ingested.signedUrl;
      else window.open(ingested.signedUrl, '_blank', 'noopener');
    } else {
      tab?.close();
      alert('This video is no longer available. Shotstack deletes rendered videos after 24 hours and it was never copied to storage.');
    }
  };

  return (
    <>
      {/* Stats */}
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 px-6 py-4 border-b border-slate-800">
        {[
          { label: 'Total', value: stats.total, icon: Film, color: 'text-slate-300' },
          { label: 'Users', value: stats.uniqueUsers, icon: Users, color: 'text-blue-400' },
          { label: 'Completed', value: stats.completed, icon: CheckCircle, color: 'text-green-400' },
          { label: 'In Progress', value: stats.inProgress, icon: Clock, color: 'text-amber-400' },
          { label: 'Failed', value: stats.failed, icon: AlertCircle, color: 'text-red-400' },
          { label: 'Funnel', value: stats.funnelPreviews, icon: Filter, color: 'text-purple-400' },
        ].map((s) => (
          <div key={s.label} className="bg-slate-900/60 border border-slate-800 rounded-lg p-3 text-center">
            <s.icon className={`w-4 h-4 mx-auto mb-1 ${s.color}`} />
            <p className={`text-lg font-bold ${s.color}`}>{s.value}</p>
            <p className="text-[10px] text-slate-500 uppercase tracking-wider">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 px-6 py-3 border-b border-slate-800 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by email or project name..."
            className="w-full pl-9 pr-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="bg-slate-900 border border-slate-700 text-slate-200 text-sm rounded-lg px-3 py-2"
        >
          <option value="">All statuses</option>
          <option value="queued">Queued</option>
          <option value="generating_clips">Generating Clips</option>
          <option value="editing">Editing</option>
          <option value="rendering">Rendering</option>
          <option value="completed">Completed</option>
          <option value="failed">Failed</option>
          <option value="expired">Expired</option>
        </select>
        <button
          onClick={refresh}
          className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
          title="Refresh"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
        <span className="text-sm text-slate-500">
          {matchTotal === 0
            ? '0 projects'
            : `Showing ${pageStart}–${pageEnd} of ${matchTotal}`}
        </span>
      </div>

      {/* Table */}
      <div className="overflow-x-auto max-h-[60vh] overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-slate-400">
            <Loader2 className="w-8 h-8 animate-spin" />
          </div>
        ) : projects.length === 0 ? (
          <div className="text-center py-20 px-6">
            <Film className="w-12 h-12 mx-auto text-slate-700 mb-4" />
            <h4 className="text-slate-300 font-medium mb-1">No projects found</h4>
            <p className="text-sm text-slate-500">
              {searchQuery || statusFilter ? 'Try adjusting your filters' : 'Projects will appear here once users create them'}
            </p>
          </div>
        ) : (
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-900/80 text-slate-400 border-b border-slate-800 sticky top-0">
              <tr>
                <th className="px-4 py-3 font-medium">Project</th>
                <th className="px-4 py-3 font-medium">Owner Email</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Quality</th>
                <th className="px-4 py-3 font-medium text-center">Images</th>
                <th className="px-4 py-3 font-medium">Type</th>
                <th className="px-4 py-3 font-medium">Created</th>
                <th className="px-4 py-3 font-medium w-28"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/60">
              {projects.map((p) => (
                <React.Fragment key={p.id}>
                  <tr className="hover:bg-slate-900/50">
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        {p.is_promoted && (
                          <span className="w-2 h-2 rounded-full bg-purple-500 shrink-0" title="Active Space" />
                        )}
                        <span className="text-slate-200 font-medium truncate max-w-[200px]" title={p.name}>
                          {p.name}
                        </span>
                      </div>
                      {p.error_message && (
                        <p className="text-[10px] text-red-400/70 truncate max-w-[200px] mt-0.5" title={p.error_message}>
                          {p.error_message}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      {p.owner_email ? (
                        <a
                          href={`mailto:${p.owner_email}`}
                          className="text-blue-400 hover:text-blue-300 hover:underline truncate max-w-[200px] block"
                          title={p.owner_email}
                        >
                          {p.owner_email}
                        </a>
                      ) : (
                        <span className="text-slate-600 italic">Anonymous</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[p.status] ?? 'bg-slate-700/60 text-slate-400'}`}>
                        {p.status}
                      </span>
                    </td>
                    <td className="px-4 py-2.5">
                      <span className={`text-xs ${
                        p.quality === 'ultra_premium' || p.quality === 'premium' ? 'text-purple-400' :
                        p.quality === 'standard' ? 'text-amber-400' :
                        p.quality === 'tester' ? 'text-emerald-400' :
                        p.quality === 'quick' ? 'text-blue-400' :
                        'text-slate-400'
                      }`}>
                        {p.quality === 'ultra_premium' ? 'Ultra' :
                          p.quality === 'premium' ? 'Premium' :
                          p.quality === 'standard' ? 'Standard' :
                          p.quality === 'quick' ? 'Quick' :
                          p.quality === 'tester' ? 'Tester' : 'Standard'}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-slate-400 text-center">{p.image_count}</td>
                    <td className="px-4 py-2.5">
                      {p.is_listing_preview ? (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-900/30 text-purple-400">
                          Funnel
                        </span>
                      ) : (
                        <span className="text-xs text-slate-500">App</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-slate-500 text-xs whitespace-nowrap">
                      {new Date(p.created_at).toLocaleDateString(undefined, { dateStyle: 'medium' })}
                      <br />
                      <span className="text-slate-600">
                        {new Date(p.created_at).toLocaleTimeString(undefined, { timeStyle: 'short' })}
                      </span>
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-1">
                        {p.image_count > 0 && (
                          <button
                            onClick={() => toggleExpanded(p.id)}
                            className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium transition-colors ${
                              expandedProjectId === p.id
                                ? 'bg-green-600/20 text-green-400 hover:bg-green-600/30'
                                : 'bg-slate-700/60 text-slate-300 hover:bg-slate-600/60'
                            }`}
                            title="View & download images"
                          >
                            <ImageIcon className="w-3 h-3" />
                            {expandedProjectId === p.id ? (
                              <ChevronUp className="w-3 h-3" />
                            ) : (
                              <ChevronDown className="w-3 h-3" />
                            )}
                          </button>
                        )}
                        {p.share_token ? (
                          <a
                            href={`${window.location.origin}/view/${p.share_token}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-blue-600/20 text-blue-400 hover:bg-blue-600/30 text-xs font-medium"
                          >
                            <Eye className="w-3 h-3" />
                          </a>
                        ) : p.status === 'completed' && p.final_video_url ? (
                          <button
                            onClick={() => playVideo(p)}
                            disabled={openingVideoId === p.id}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-slate-700/60 text-slate-300 hover:bg-slate-600/60 disabled:opacity-50 text-xs font-medium"
                            title="Play video"
                          >
                            {openingVideoId === p.id ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <Play className="w-3 h-3" />
                            )}
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                  {expandedProjectId === p.id && (
                    <tr className="bg-slate-900/30">
                      <ImageRow projectId={p.id} projectName={p.name} />
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {matchTotal > PROJECTS_PAGE_SIZE && (
        <div className="flex items-center justify-end gap-2 px-6 py-3 border-t border-slate-800">
          <button
            type="button"
            onClick={() => setPage((current) => Math.max(0, current - 1))}
            disabled={page === 0 || loading}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-slate-800 text-slate-300 text-xs font-medium disabled:opacity-40"
          >
            <ChevronLeft className="w-3 h-3" />
            Previous
          </button>
          <button
            type="button"
            onClick={() => setPage((current) => current + 1)}
            disabled={(page + 1) * PROJECTS_PAGE_SIZE >= matchTotal || loading}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-slate-800 text-slate-300 text-xs font-medium disabled:opacity-40"
          >
            Next
            <ChevronRight className="w-3 h-3" />
          </button>
        </div>
      )}
    </>
  );
}

const SECTION_META: Record<AdminSection, { title: string; description: string }> = {
  projects: { title: 'Projects', description: 'All video projects across users' },
  staging_animations: { title: 'Staging Animations', description: 'All staging animations created by users' },
  models: { title: 'Models and pricing', description: 'fal model catalogue, quality tiers and profitability' },
  staging_animation: { title: 'Staging Animation settings', description: 'Staging Animation price, styles, and video model' },
};

export default function AdminPanel({ onClose, adminRole }: AdminPanelProps) {
  const [section, setSection] = useState<AdminSection>('projects');

  return (
    <AdminShell<AdminSection>
      scope="ImageMotion"
      role={adminRole}
      onBack={onClose}
      variant="overlay"
      tabs={[
        { id: 'projects', label: 'Projects', icon: FolderKanban },
        { id: 'staging_animations', label: 'Staging Animations', icon: Clapperboard },
        { id: 'models', label: 'Models and pricing', icon: Layers },
        { id: 'staging_animation', label: 'Staging Animation settings', icon: Film },
      ]}
      activeTab={section}
      onTabChange={setSection}
      title={SECTION_META[section].title}
      description={SECTION_META[section].description}
    >
      {section === 'projects' ? (
        <ProjectsSection />
      ) : section === 'staging_animations' ? (
        <StagingAnimationsList />
      ) : section === 'staging_animation' ? (
        <StagingAnimationSettings />
      ) : (
        <ModelsPricingPanel />
      )}
    </AdminShell>
  );
}
